<template>
    <div>
        <internal-header heading="Users" :show-button="true" button-text="Add User" :button-link="createClick" />
        <div class="tw-flex tw-gap-3 tw-mt-2">
            <div class="tw-w-1/3">
                <v-text-field
                    v-model="search"
                    label="Search Users"
                    outlined
                    dense
                    append-icon="mdi-magnify"
                    clearable
                ></v-text-field>
            </div>
            <v-text-field
                v-model="filters.local"
                label="Local"
                outlined
                dense
                clearable
            ></v-text-field>
            <v-select
                label="Classification"
                :items="valuelist.CLASSIFICATIONS"
                v-model="filters['status.id']"
                outlined
                dense
                clearable
            ></v-select>
            <v-autocomplete class="tw-w-full md:tw-w-1/6"
                            label="Job Category"
                            :items="values_categories"
                            v-model="filters.category"
                            multiple
                            outlined
                            dense
                            clearable
            ></v-autocomplete>
            <v-select class="tw-w-full md:tw-w-1/6"
                      label="Province"
                      :items="values_provinces"
                      v-model="filters.province"
                      multiple
                      outlined
                      dense
                      clearable
            ></v-select>

            <v-btn @click="advancedSearchModal = !advancedSearchModal" class="main-btn">
                Advanced
            </v-btn>

            <v-btn @click="exportMembersModal = !exportMembersModal" class="main-btn">
                Export
            </v-btn>

<!--            <div @click="exportAsPdf()" class="tw-text-black tw-underline tw-text-xs tw-font-normal tw-text-link tw-cursor-pointer">Print List</div>-->

        </div>
        <smart-table
            id="members_list"
            endpoint="members.list"
            :pdf_endpoint="route('reports.membershiplist.queue', 'pdf').url()"
            :headers="headers"
            :search="search"
            :multi-sort="true"
            :items-per-page="20"
            :display_fields="displayed_column_names"
            :footer-props="{
                'items-per-page-options': [5, 10, 20, 30]
            }"
            flat
            class="row-pointer"
            @click:row="showMember"
            ref="members_list"
        >
            <template v-slot:item.id="{ isMobile, item, header, value, index}">
                {{ item.id}}
            </template>
            <template v-slot:item.first_name="{ isMobile, item, header, value, index}">
                {{ item.first_name}}
            </template>
            <template v-slot:item.last_name="{ isMobile, item, header, value, index}">
                {{ item.last_name}}
            </template>
            <template v-slot:item.functions_active.title_function_id="{ isMobile, item, header, value, index}">
                {{ getCategoriesForDisplay(item) }}
            </template>
            <template v-slot:item.status.classification_name="{ isMobile, item, header, value, index}">
                {{ item.status?.classification_name }}
            </template>
            <template v-slot:item.telephones.telephone_fax="{ isMobile, item, header, value, index}">
                {{ generateList(item.telephones, 'telephone_fax') }}
            </template>
            <template v-slot:item.emails.email="{ isMobile, item, header, value, index}">
                {{ generateList(item.emails, 'email') }}
            </template>
            <template v-slot:item.mailing_address.province_id="{ isMobile, item, header, value, index}">
                {{ generateList(item.mailing_address, 'province_id', valuelist.PROVINCES) }}
            </template>
            <template v-slot:item.actions="{ isMobile, item, header, value, index}">
                <v-btn
                    v-show="$root.getUserRole() === constants?.USER_ROLES?.ROLE_ADMIN && item.impersonatable && item.role !== constants?.USER_ROLES?.ROLE_ADMIN"
                    fab
                    small
                    dark
                    color="primary"
                    @click.prevent.stop="impersonateUser(item)"
                >
                    <v-icon dark>
                    mdi-target-account
                    </v-icon>
                </v-btn>
            </template>
        </smart-table>


        <MembersAdvancedSearchModal
            v-model="advancedSearchModal"
            :filtersProp="filters"
            :constants="constant"
            :valuelists="valuelists"
            @advanced_search_closed="closeAdvancedSearchModal"
        ></MembersAdvancedSearchModal>
        <export-members-modal
            v-model="exportMembersModal"
            :filters="generateFiltersWithSearch()"
        ></export-members-modal>
    </div>
</template>

<script>
    import SmartTable from "@/Generic/SmartTable";
    import MembersAdvancedSearchModal from "@/Modals/MembersAdvancedSearchModal";
    import AppLayout from "@/Layouts/AppLayout.vue";
    import InternalHeader from "@/Generic/InternalHeader";
    import ExportMembersModal from "@/Modals/ExportMembersModal.vue";

    export default {
        name: "MembersIndex",
        layout: (h, page) => {
            return h(AppLayout, [
                page
            ])
        },
        components: {
            SmartTable,
            MembersAdvancedSearchModal,
            InternalHeader,
            ExportMembersModal,
        },
        remember: {
            data: [
                'items'
            ]
        },
        props: {
            'constants': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
            'valuelists': {
                type: Object,
                required: false,
                default: function () {
                    return undefined
                }
            },
        },
        data() {
            return {
                search: null,
                constant: this.constants,
                valuelist: this.valuelists,
                filters: {
                    local: undefined,
                    'telephones.telephone_fax': undefined,
                    nas: undefined,
                    dob: undefined,
                    address: undefined,
                    gender: undefined,
                    casl: undefined,
                    web_access: undefined,
                    producer_access: undefined,
                    age_25_and_65: undefined,
                    ia_retiree: undefined,
                    region: undefined,
                    role: undefined,
                    'status.id': undefined,
                    category: undefined,
                    province: undefined,
                },
                advancedSearchModal: false,
                exportMembersModal: false,
            }
        },
        computed: {
            headers() {
                return [
                    {value: 'id',                           text: 'User ID',                        align: `start`, sortable: true,    filterable: true,   search: this?.filters?.id, optional: false, },
                    {value: 'first_name',                   text: 'First Name',                     align: `start`, sortable: true,    filterable: true,   search: this?.filters?.first_name, optional: false, },     // ` d-none` Makes the column disappear (display:none)
                    {value: 'last_name',                    text: 'Last Name',                      align: `start`, sortable: true,    filterable: true,   search: this?.filters?.last_name, optional: false, },
                    {value: 'role',                         text: 'Role',                           align: `start`, sortable: true,    filterable: true,   search: this?.filters?.role, optional: false, },
                    {value: 'functions_active.title_function_id',   text: 'Categories',             align: `start`, sortable: false,   filterable: true,   search: this?.filters?.category  },
                    {value: 'status.classification_name',   text: 'Classification',                 align: `start`, sortable: false,   filterable: true,   optional: false, },
                    {value: 'telephones.telephone_fax',     text: 'Phone',                          align: `start`, sortable: false,   filterable: true,   search: this?.filters?.["telephones.telephone_fax"], optional: false, },
                    {value: 'emails.email',                 text: 'Email',                          align: `start`, sortable: false,   filterable: true,   search: this?.filters?.emails, optional: false, },
                    {value: 'mailing_address.province_id',  text: 'Mailing Province',               align: `start`, sortable: false,   filterable: true,   optional: false, },
                    {value: 'local',                        text: 'Local',                          align: `start`, sortable: false,   filterable: true,   search: this?.filters?.local, optional: false, },
                    {value: 'nas',                          text: 'SIN',                            align: `start`, sortable: false,   filterable: true,   search: this?.filters?.nas, optional: false, },
                    {value: 'dob',                          text: 'DOB',                            align: `start`, sortable: false,   filterable: true,   search: this?.filters?.dob, optional: false, },
                    {value: 'address',                      text: 'Address',                        align: `start`, sortable: false,   filterable: true,   search: this?.filters?.address, optional: false, },
                    {value: 'genders.id',                    text: 'Gender',                         align: `start`, sortable: false,   filterable: true,   search: this?.filters?.gender, optional: false, },
                    {value: 'casl',                         text: 'CASL',                           align: `start`, sortable: false,   filterable: true,   search: this?.filters?.casl, optional: false, },
                    {value: 'web_access',                   text: 'Web Access',                     align: `start`, sortable: false,   filterable: true,   search: this?.filters?.web_access, optional: false, },
                    {value: 'producer_access',              text: 'Producer Access',                align: `start`, sortable: false,   filterable: true,   search: this?.filters?.producer_access, optional: false, },
                    {value: 'age_25_and_65',                text: '25/65',                          align: `start`, sortable: false,   filterable: true,   search: this?.filters?.age_25_and_65, optional: false, },
                    {value: 'ia_retiree',                   text: 'IA Retiree',                     align: `start`, sortable: false,   filterable: true,   search: this?.filters?.ia_retiree, optional: false, },
                    {value: 'region',                       text: 'Region',                         align: `start`, sortable: false,   filterable: true,   search: this?.filters?.region, optional: false, },
                    {value: 'status.id',                    text: 'Classification',                 align: `start`, sortable: false,   filterable: true,   search: this?.filters?.["status.id"], optional: false, },
                    {value: 'actions',                      text: 'Actions',                        align: `start`, sortable: false,   filterable: false,  optional: false,  },
                    {value: 'addresses.province_id',        text: 'Province',                       align: `start`, sortable: false,   filterable: true,   search: this?.filters?.province, optional: false, },
                ]
            },
            displayed_column_names() {
                let headers_to_display = this.headers.filter(header => this.should_column_be_displayed(header.value))
                return headers_to_display.map(header => header.value)
            },
            values_categories() {
                if (this?.valuelists?.TITLE_FUNCTIONS_WEBSITE) {
                    return Object.values(this.valuelists?.TITLE_FUNCTIONS_WEBSITE)
                }
                return []
            },
            values_provinces() {
                if (this?.valuelists?.PROVINCES) {
                    return Object.values(this.valuelists?.PROVINCES)
                }
                return []
            },
        },
        methods: {
            should_column_be_displayed(column) {
                let required_headers = ['id', 'first_name', 'last_name', 'functions_active.title_function_id', 'status.classification_name', 'telephones.telephone_fax', 'emails.email', 'mailing_address.province_id', 'actions']
                return required_headers.includes(column)
            },
            getCategoriesForDisplay(item) {
                if (!item?.functions_active?.length) return ''
                let refined = item.functions_active.map((title) => title.function_name)
                return refined.join(', ')
            },
            generateList(object, key, valuelist) {
                let list = []
                if (valuelist === undefined) {
                    list = object?.map(obj => eval(`obj.${key}`))
                } else {
                    list = object?.map(obj => this.findValuelist(valuelist, eval(`obj.${key}`)).text)
                }
                let listWithoutDuplicates = [...new Set(list)]
                return listWithoutDuplicates.join(', ')
            },
            closeAdvancedSearchModal() {
                this.advancedSearchModal = false
            },
            generateFiltersWithSearch() {
                let filtersWithSearch = structuredClone(this.filters)
                filtersWithSearch.search = this.search
                return filtersWithSearch
            },
            showMember(member) {
                this.$inertia.visit( route('members.show', member.id) )
            },
            createClick() {
                this.$inertia.visit(route('members.create'))
            },
            findValuelist(list, id) {
                if (list && id) {
                    return list.find(obj => obj.value === id)
                } else {
                    return ""
                }
            },
            impersonateUser(user){
                this.$inertia.visit(route('user.cloak-as', user))
            },
            async exportAsPdf() {
                await this.$refs.members_list.getPdf()
            },
        }
    }
</script>

<style>
.row-pointer tbody tr {
    cursor: pointer;
}
</style>
